/**
 * Header Navigation Controller
 * Implements WAI-ARIA Menubar Navigation pattern
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/menubar/examples/menubar-navigation/
 */
;(function () {
  'use strict'

  // Media queries
  const hoverMediaQuery = window.matchMedia('(hover: hover)')
  const desktopMediaQuery = window.matchMedia('(min-width: 1400px)')

  // DOM Elements
  const header = document.querySelector('.rd-header')
  const hamburger = document.querySelector('.rd-hamburger')
  const nav = document.querySelector('.rd-header-nav')

  // Exit if required elements not found
  if (!nav) {
    console.warn('Header: Navigation element not found')
    return
  }

  // Menu elements
  const menubar = nav.querySelector('[role="menubar"]')
  const menubarItems = menubar
    ? Array.from(menubar.querySelectorAll(':scope > li > [role="menuitem"]'))
    : []
  const dropdownButtons = menubarItems.filter(
    (item) => item.getAttribute('aria-haspopup') === 'true'
  )

  // State
  let isMenuOpen = false
  let currentMenubarIndex = 0
  let hoverTimeout = null

  /**
   * Check if device supports hover
   */
  function canHover() {
    return hoverMediaQuery.matches
  }

  /**
   * Check if we're on desktop layout
   */
  function isDesktop() {
    return desktopMediaQuery.matches
  }

  /**
   * Initialize header functionality
   */
  function init() {
    setupHamburger()
    setupDropdowns()
    setupMenubarKeyboard()
    setupOutsideClick()
    setupEscapeKey()
    setupMediaQueryListeners()

    // Set initial tabindex for menubar items (roving tabindex)
    menubarItems.forEach((item, index) => {
      item.setAttribute('tabindex', index === 0 ? '0' : '-1')
    })
  }

  // ============================================
  // MEDIA QUERY LISTENERS
  // ============================================

  function setupMediaQueryListeners() {
    // Handle desktop/mobile layout change
    desktopMediaQuery.addEventListener('change', (e) => {
      if (e.matches) {
        // Switched to desktop - close mobile menu
        closeMobileMenu()
      } else {
        // Switched to mobile - close dropdowns
        closeAllDropdowns()
      }
    })
  }

  // ============================================
  // HAMBURGER MENU (Mobile)
  // ============================================

  function setupHamburger() {
    if (!hamburger) return

    hamburger.addEventListener('click', toggleMenu)
  }

  function toggleMenu() {
    // Only toggle on non-desktop
    if (isDesktop()) return

    isMenuOpen = !isMenuOpen

    // Update hamburger state
    hamburger.setAttribute('aria-expanded', String(isMenuOpen))
    hamburger.setAttribute(
      'aria-label',
      isMenuOpen ? 'Close menu' : 'Open menu'
    )

    // Update nav state
    nav.setAttribute('data-open', String(isMenuOpen))

    // Update header state
    if (header) {
      header.setAttribute('data-open', String(isMenuOpen))
    }

    // Manage focus and body scroll
    if (isMenuOpen) {
      const firstFocusable = menubarItems[0]
      if (firstFocusable) {
        firstFocusable.focus()
      }
      document.body.classList.add('menu-open')
    } else {
      closeAllDropdowns()
      document.body.classList.remove('menu-open')
    }
  }

  function closeMobileMenu() {
    if (!isMenuOpen) return

    isMenuOpen = false
    hamburger?.setAttribute('aria-expanded', 'false')
    hamburger?.setAttribute('aria-label', 'Open menu')
    nav.setAttribute('data-open', 'false')
    header?.setAttribute('data-open', 'false')
    document.body.classList.remove('menu-open')
    closeAllDropdowns()
  }

  // ============================================
  // DROPDOWN SUBMENUS
  // ============================================

  function setupDropdowns() {
    dropdownButtons.forEach((button) => {
      const dropdownId = button.getAttribute('aria-controls')
      const dropdown = document.getElementById(dropdownId)
      const parentLi = button.closest('.rd-header-nav__item')

      if (!dropdown || !parentLi) return

      // Click handler (only for touch devices without hover support)
      button.addEventListener('click', (e) => {
        if (!canHover()) {
          handleDropdownClick(e)
        }
      })

      // Hover handlers - only for devices that support hover
      parentLi.addEventListener('mouseenter', () => {
        if (!canHover()) return
        clearTimeout(hoverTimeout)
        closeAllDropdowns()
        openDropdown(button, dropdown, false)
      })

      parentLi.addEventListener('mouseleave', () => {
        if (!canHover()) return
        hoverTimeout = setTimeout(() => {
          closeDropdown(button, dropdown)
        }, 200)
      })

      // Keep dropdown open when hovering over it
      dropdown.addEventListener('mouseenter', () => {
        if (!canHover()) return
        clearTimeout(hoverTimeout)
      })

      dropdown.addEventListener('mouseleave', () => {
        if (!canHover()) return
        hoverTimeout = setTimeout(() => {
          closeDropdown(button, dropdown)
        }, 200)
      })
    })
  }

  function handleDropdownClick(e) {
    e.preventDefault()
    const button = e.currentTarget
    const dropdownId = button.getAttribute('aria-controls')
    const dropdown = document.getElementById(dropdownId)

    if (!dropdown) return

    const isOpen = button.getAttribute('aria-expanded') === 'true'

    if (isOpen) {
      closeDropdown(button, dropdown)
    } else {
      closeAllDropdowns()
      // On touch devices, focus first item; on hover devices, don't auto-focus
      openDropdown(button, dropdown, !canHover())
    }
  }

  function openDropdown(button, dropdown, focusFirst = true) {
    button.setAttribute('aria-expanded', 'true')
    dropdown.setAttribute('data-open', 'true')
    dropdown.setAttribute('aria-hidden', 'false')

    if (focusFirst) {
      const firstItem = dropdown.querySelector('[role="menuitem"]')
      if (firstItem) {
        firstItem.focus()
      }
    }
  }

  function closeDropdown(button, dropdown) {
    button.setAttribute('aria-expanded', 'false')
    dropdown.setAttribute('data-open', 'false')
    dropdown.setAttribute('aria-hidden', 'true')
  }

  function closeAllDropdowns() {
    clearTimeout(hoverTimeout)
    dropdownButtons.forEach((button) => {
      const dropdownId = button.getAttribute('aria-controls')
      const dropdown = document.getElementById(dropdownId)
      if (dropdown) {
        closeDropdown(button, dropdown)
      }
    })
  }

  function getOpenDropdown() {
    for (const button of dropdownButtons) {
      if (button.getAttribute('aria-expanded') === 'true') {
        const dropdownId = button.getAttribute('aria-controls')
        return {
          button,
          dropdown: document.getElementById(dropdownId),
        }
      }
    }
    return null
  }

  // ============================================
  // KEYBOARD NAVIGATION
  // ============================================

  function setupMenubarKeyboard() {
    if (!menubar) return

    menubar.addEventListener('keydown', handleMenubarKeydown)
  }

  function handleMenubarKeydown(e) {
    const { target } = e
    const openDropdownData = getOpenDropdown()

    // Check if focus is in a submenu
    const isInSubmenu = target.closest('[role="menu"]')

    if (isInSubmenu && openDropdownData) {
      handleSubmenuKeydown(e, openDropdownData)
    } else if (menubarItems.includes(target)) {
      handleMenubarItemKeydown(e)
    }
  }

  /**
   * Handle keyboard navigation on menubar items
   */
  function handleMenubarItemKeydown(e) {
    const { key, target } = e

    switch (key) {
      case 'ArrowRight':
        e.preventDefault()
        focusNextMenubarItem()
        break

      case 'ArrowLeft':
        e.preventDefault()
        focusPreviousMenubarItem()
        break

      case 'ArrowDown':
        e.preventDefault()
        if (target.getAttribute('aria-haspopup') === 'true') {
          openDropdownAndFocusFirst(target)
        }
        break

      case 'ArrowUp':
        e.preventDefault()
        if (target.getAttribute('aria-haspopup') === 'true') {
          openDropdownAndFocusLast(target)
        }
        break

      case 'Enter':
      case ' ':
        if (target.getAttribute('aria-haspopup') === 'true') {
          e.preventDefault()
          openDropdownAndFocusFirst(target)
        }
        // For regular links, let default behavior happen
        break

      case 'Home':
        e.preventDefault()
        focusMenubarItem(0)
        break

      case 'End':
        e.preventDefault()
        focusMenubarItem(menubarItems.length - 1)
        break

      default:
        // Character navigation
        if (key.length === 1 && /[a-zA-Z]/.test(key)) {
          e.preventDefault()
          focusMenubarItemByChar(key)
        }
    }
  }

  /**
   * Handle keyboard navigation within submenus
   */
  function handleSubmenuKeydown(e, { button, dropdown }) {
    const { key, target } = e
    const menuItems = Array.from(dropdown.querySelectorAll('[role="menuitem"]'))
    const currentIndex = menuItems.indexOf(target)

    switch (key) {
      case 'ArrowDown':
        e.preventDefault()
        focusSubmenuItem(menuItems, (currentIndex + 1) % menuItems.length)
        break

      case 'ArrowUp':
        e.preventDefault()
        focusSubmenuItem(
          menuItems,
          (currentIndex - 1 + menuItems.length) % menuItems.length
        )
        break

      case 'ArrowRight':
        e.preventDefault()
        closeDropdown(button, dropdown)
        focusNextMenubarItem()
        // Open next dropdown if it has one
        {
          const nextItem = menubarItems[currentMenubarIndex]
          if (nextItem && nextItem.getAttribute('aria-haspopup') === 'true') {
            openDropdownAndFocusFirst(nextItem)
          }
        }
        break

      case 'ArrowLeft':
        e.preventDefault()
        closeDropdown(button, dropdown)
        focusPreviousMenubarItem()
        // Open previous dropdown if it has one
        {
          const prevItem = menubarItems[currentMenubarIndex]
          if (prevItem && prevItem.getAttribute('aria-haspopup') === 'true') {
            openDropdownAndFocusFirst(prevItem)
          }
        }
        break

      case 'Home':
        e.preventDefault()
        focusSubmenuItem(menuItems, 0)
        break

      case 'End':
        e.preventDefault()
        focusSubmenuItem(menuItems, menuItems.length - 1)
        break

      case 'Escape':
        e.preventDefault()
        closeDropdown(button, dropdown)
        button.focus()
        break

      case 'Enter':
      case ' ':
        // Let links navigate naturally
        if (target.tagName === 'A') {
          // Default behavior - navigate to link
        }
        break

      default:
        // Character navigation within submenu
        if (key.length === 1 && /[a-zA-Z]/.test(key)) {
          e.preventDefault()
          focusSubmenuItemByChar(menuItems, currentIndex, key)
        }
    }
  }

  // ============================================
  // FOCUS MANAGEMENT
  // ============================================

  function focusMenubarItem(index) {
    // Update roving tabindex
    menubarItems[currentMenubarIndex]?.setAttribute('tabindex', '-1')
    currentMenubarIndex = index
    menubarItems[currentMenubarIndex]?.setAttribute('tabindex', '0')
    menubarItems[currentMenubarIndex]?.focus()
  }

  function focusNextMenubarItem() {
    const nextIndex = (currentMenubarIndex + 1) % menubarItems.length
    focusMenubarItem(nextIndex)
  }

  function focusPreviousMenubarItem() {
    const prevIndex =
      (currentMenubarIndex - 1 + menubarItems.length) % menubarItems.length
    focusMenubarItem(prevIndex)
  }

  function focusMenubarItemByChar(char) {
    const lowerChar = char.toLowerCase()
    const startIndex = currentMenubarIndex + 1

    // Search from current position to end
    for (let i = startIndex; i < menubarItems.length; i++) {
      if (getItemText(menubarItems[i]).toLowerCase().startsWith(lowerChar)) {
        focusMenubarItem(i)
        return
      }
    }

    // Wrap around and search from beginning
    for (let i = 0; i < startIndex; i++) {
      if (getItemText(menubarItems[i]).toLowerCase().startsWith(lowerChar)) {
        focusMenubarItem(i)
        return
      }
    }
  }

  function focusSubmenuItem(menuItems, index) {
    menuItems[index]?.focus()
  }

  function focusSubmenuItemByChar(menuItems, currentIndex, char) {
    const lowerChar = char.toLowerCase()
    const startIndex = currentIndex + 1

    // Search from current position to end
    for (let i = startIndex; i < menuItems.length; i++) {
      if (getItemText(menuItems[i]).toLowerCase().startsWith(lowerChar)) {
        menuItems[i].focus()
        return
      }
    }

    // Wrap around and search from beginning
    for (let i = 0; i < startIndex; i++) {
      if (getItemText(menuItems[i]).toLowerCase().startsWith(lowerChar)) {
        menuItems[i].focus()
        return
      }
    }
  }

  function getItemText(element) {
    // Get visible text, excluding visually-hidden content
    const clone = element.cloneNode(true)
    clone.querySelectorAll('.visually-hidden').forEach((el) => el.remove())
    return clone.textContent?.trim() || ''
  }

  function openDropdownAndFocusFirst(button) {
    const dropdownId = button.getAttribute('aria-controls')
    const dropdown = document.getElementById(dropdownId)
    if (dropdown) {
      closeAllDropdowns()
      openDropdown(button, dropdown, true)
    }
  }

  function openDropdownAndFocusLast(button) {
    const dropdownId = button.getAttribute('aria-controls')
    const dropdown = document.getElementById(dropdownId)
    if (dropdown) {
      closeAllDropdowns()
      openDropdown(button, dropdown, false)
      const menuItems = dropdown.querySelectorAll('[role="menuitem"]')
      const lastItem = menuItems[menuItems.length - 1]
      if (lastItem) {
        lastItem.focus()
      }
    }
  }

  // ============================================
  // OUTSIDE CLICK & ESCAPE
  // ============================================

  function setupOutsideClick() {
    document.addEventListener('click', (e) => {
      // Close dropdowns if clicking outside nav
      if (!nav.contains(e.target) && !hamburger?.contains(e.target)) {
        closeAllDropdowns()

        // Close mobile menu if open (only on non-desktop)
        if (isMenuOpen && !isDesktop()) {
          closeMobileMenu()
        }
      }
    })
  }

  function setupEscapeKey() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const openDropdownData = getOpenDropdown()

        if (openDropdownData) {
          closeDropdown(openDropdownData.button, openDropdownData.dropdown)
          openDropdownData.button.focus()
        } else if (isMenuOpen && !isDesktop()) {
          closeMobileMenu()
          hamburger?.focus()
        }
      }
    })
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
